<template>
  <div class="eligibility-uploader">
    <div class="import-card">
      <div class="card-header">
        <h2>Eligibility List SFTP Feed</h2>
        <div class="download-helper-bar">
          <div class="orgs-selection">
            <select
              v-if="user.isStaff && !organizations.refreshing"
              class="organizations"
              @change="onDownloadOrganizationSelection"
            >
              <option value="" disabled :selected="selectedDownloadOrganization.id === null"
                >Select Organization</option
              >
              <option
                v-for="org in organizations.list"
                :key="org.id"
                :value="org.id"
                :selected="selectedDownloadOrganization.id === org.id"
                >{{ org.name }}</option
              >
            </select>
            <div
              v-else-if="user.isStaff && organizations.refreshing"
              class="organizations placeholder"
            >
              <LoadSpinner />
            </div>

            <select
              v-if="
                (user.isAdmin && !user.subOrganization && downloadSuborganizations().length) ||
                  (user.isStaff && downloadSuborganizations().length)
              "
              class="suborganizations"
              @change="onSubOrganizationSelection"
            >
              <option value="" disabled :selected="selectedSubOrganization.id === null"
                >Select Sub Organization</option
              >
              <option
                v-for="sub_org in downloadSuborganizations()"
                :key="sub_org.id"
                :value="sub_org.id"
                :selected="selectedSubOrganization.id === sub_org.id"
                >{{ sub_org.name }}</option
              >
            </select>
          </div>
          <md-button @click="handleEligibilityListDownload">
            <i>Download Current Eligibility List</i>
          </md-button>
          <md-button @click="handleTemplateDownload">
            <i>Download Template File</i>
          </md-button>
        </div>
      </div>
      <div class="card-body">
        <div class="instructions">
          <div class="bold">
            You have two options for updating your list of eligible unique IDs: (1) manual upload
            tool (see the Upload List tab) and (2) the automated SFTP feed (described below).
          </div>
          <br />
          <br />
          <h5>File format</h5>
          <ul class="copy-list">
            <li>
              Each time you send a new file, please ensure it is the
              <div class="emphasis">full</div>
              list of unique IDs eligible to request funds from the Grant Circle.
            </li>
            <li>
              The file should
              <div class="emphasis">have two columns:</div>
              column A, containing the list of eligible unique IDs, and column B, containing the
              list of email addresses corresponding to each unique ID. Email addresses are used to
              send a notification of account creation for security purposes. If you do not have
              email addresses for all unique IDs, blanks are accepted, though individuals for whom
              we do not have an email will not receive the security notification upon account
              creation. (Note: All users will receive the standard account creation notification to
              the email they provide during account creation.)
            </li>
            <li>
              Column A should have a header of
              <div class="emphasis">Unique ID</div>
              , and column B should have a header of
              <div class="emphasis">Email Address</div>
              . Make sure there are no extra spaces before or after the phrases in the header
              labels. (Values for each row in column B are optional.)
            </li>
            <li>
              Make sure there are no unnecessary spaces before or after the ID values in Column A.
            </li>
            <li>There should only be one unique ID per row.</li>
            <li>There should not be any duplicate unique IDs or email addresses.</li>
            <li>
              The file should be in CSV format. If you are working in Excel, select 'save as' and
              choose CSV as the format.
            </li>
            <li>The file name should not have any spaces in it.</li>
            <li>
              Please note that Excel may change the formatting of your unique IDs. In particular, a
              common problem is the removal of leading zeros from unique IDs containing only
              numbers.
            </li>
          </ul>
          <h5>How to set up the SFTP feed</h5>
          <ul class="copy-list">
            <li>
              Step 1: Create a ssh-rsa public/private key pair.
            </li>
            <li>
              Step 2: Email the public key to your Grant Circle relationship manager and/or
              info@grantcircles.org. The Grant Circle team will provide you with your username to
              access the SFTP server.
            </li>
            <li>
              Step 3: If you haven't already, send your Grant Circle relationship manager and/or
              info@grantcircles.org the email address(s) of the person or people on your team who
              should receive file update confirmation and exception emails.
            </li>
            <li>
              Step 4: Connect to the SFTP server using your preferred SFTP client. (You should be
              automatically connected to your company's directory and be able to immediately push
              files to the SFTP server.)
            </li>
            <li>
              Step 5: Any file pushed will be processed by the file feed. You will receive an email
              with the result of that file processing (either a confirmation of successful update or
              an exception email with instructions about how to remediate).
            </li>
          </ul>
          <h5>How it works</h5>
          <ul class="copy-list">
            <li>
              Each uploaded list will be compared to the list of eligible IDs already in the
              database.
              <ul>
                <li>
                  New IDs, never before on the list: will be added to the eligible list in the
                  database.
                </li>
                <li>
                  New IDs, previously onboarded, removed from list, and marked inactive: will be
                  added to the eligible list and marked as active, reinstating the individual's
                  ability to log in, request, and receive funds, given previous activity.
                </li>
                <li>
                  Missing IDs, not yet onboarded: will be removed from the eligible list in the
                  database.
                </li>
                <li>
                  Missing IDs, already onboarded and active: will be marked as inactive in the
                  database, preventing individuals from logging in, requesting, and receiving funds.
                </li>
                <li>
                  Existing IDs (common to both new and existing lists): will remain unchanged.
                </li>
                <li>
                  Existing IDs with new or different email address: will remain on the list and
                  email address will be updated.
                </li>
                <li>
                  If an ID is on the list more than once, each time associated with the same email
                  value, it will only be counted once.
                </li>
                <li>
                  If duplicates in column A are matched with different values in column B, the list
                  will generate an error.
                </li>
                <li>
                  If duplicates in column B are matched with different values in column A, the list
                  will generate an error.
                </li>
              </ul>
            </li>
            <li>
              When we receive a new list, we will send you a success confirmation email if the list
              was uploaded with no issues. The message will summarize the changes that were made as
              a result of the list upload (number of new IDs added, number removed, number marked as
              inactive, number reinstated as active, etc).
            </li>
            <li>
              If the file received is in the wrong format, contains duplicates in either column
              (blanks are not considered duplicates), or generates another type of error, the
              existing eligibility list wil not be changed. You will receive an exception notice via
              email alerting you to the issue and requesting that you supply a new list.
            </li>
            <li>
              If the file received will result in a change to more than half of existing eligible
              IDs, you will receive an email asking you to review the file before any changes are
              made.
            </li>
          </ul>
        </div>
      </div>
      <div class="selections-container">
        <h5>Eligibility List Update History</h5>
        <select
          v-if="user.isStaff && !organizations.refreshing"
          class="organizations"
          @change="onFileFeedOrganizationSelection"
        >
          <option value="" disabled :selected="selectedFileFeedOrganization.id === null"
            >Select Organization</option
          >
          <option
            v-for="org in organizations.list"
            :key="org.id"
            :value="org.id"
            :selected="selectedFileFeedOrganization.id === org.id"
            >{{ org.name }}</option
          >
        </select>
        <div v-else-if="user.isStaff && organizations.refreshing" class="organizations placeholder">
          <LoadSpinner />
        </div>
      </div>
      <md-table>
        <md-table-row class="header-row">
          <md-table-head>
            File Name
          </md-table-head>
          <md-table-head
            v-if="
              (user.isAdmin && !user.subOrganization && fileFeedSuborganizations().length) ||
                (user.isStaff && fileFeedSuborganizations().length)
            "
          >
            Sub Organization
          </md-table-head>
          <md-table-head>
            Received
          </md-table-head>
          <md-table-head>
            Import Type
          </md-table-head>
          <md-table-head>
            Import Status
          </md-table-head>
          <md-table-head>
            Active Codes Pre-Import
          </md-table-head>
          <md-table-head>
            Active Codes Post-Import
          </md-table-head>
        </md-table-row>
        <md-table-row
          class="body-row clickable-upload"
          @click="showFileUpload(employeeCodeImport.id)"
          v-for="employeeCodeImport in filterBySelectedOrganization(
            employeeCodeImports.list,
            selectedFileFeedOrganization.id,
          )"
          :key="employeeCodeImport.id"
        >
          <md-table-cell>
            <div class="name-and-status">
              <div v-if="employeeCodeImport.importStatus === 'MASS_CHANGE'">
                <md-icon>
                  warning
                </md-icon>
              </div>
              {{ employeeCodeImport.fileName }}
            </div>
          </md-table-cell>
          <md-table-cell
            v-if="
              (user.isAdmin && !user.subOrganization && fileFeedSuborganizations().length) ||
                (user.isStaff && fileFeedSuborganizations().length)
            "
          >
            {{ employeeCodeImport.subOrganizationName }}
          </md-table-cell>
          <md-table-cell>{{
            employeeCodeImport.datetimeCreated | formatDateTimeZone
          }}</md-table-cell>
          <md-table-cell>{{ options.IMPORT_TYPES[employeeCodeImport.importType] }}</md-table-cell>
          <md-table-cell>{{
            options.IMPORT_STATUSES[employeeCodeImport.importStatus]
          }}</md-table-cell>
          <md-table-cell>{{ employeeCodeImport.activeCodesPreImport }}</md-table-cell>
          <md-table-cell>{{ employeeCodeImport.activeCodesPostImport }}</md-table-cell>
        </md-table-row>
      </md-table>
    </div>
    <Modal v-if="modal.isOpen" dimmed @close-modal="modal.isOpen = false">
      <div class="form-container card-shape modal-window">
        <div class="dry-results">
          <h2 v-if="!isConfirmDisabled()">Please Confirm</h2>
          <div class="top-margin">The file you uploaded contains:</div>
          <ul>
            <li v-if="failedForErrors(dryRun)">
              <div class="emphasis">{{ getValueLengthFromDryRun(dryRun, 'errors') }}</div>
              errors
              <template v-if="getValueLengthFromDryRun(dryRun, 'errors')">
                <div
                  :style="{ display: 'inline' }"
                  v-if="modal.showErrors"
                  @click="modal.showErrors = false"
                >
                  <md-icon>
                    visibility
                  </md-icon>
                </div>
                <div :style="{ display: 'inline' }" v-else @click="modal.showErrors = true">
                  <md-icon>
                    visibility_off
                  </md-icon>
                </div>
              </template>
              <ol v-if="modal.showErrors" class="code-list">
                <li v-for="(error, index) in getValueFromDryRun(dryRun, 'errors')" :key="index">
                  {{ error }}
                </li>
              </ol>
            </li>
            <!-- Codes To Be Created -->
            <li v-if="!failedForErrors(dryRun)">
              <div class="emphasis">{{ getValueLengthFromDryRun(dryRun, 'toCreate') || 0 }}</div>
              completely new
              {{ getValueLengthFromDryRun(dryRun, 'toCreate') === 1 ? 'ID' : 'IDs' }} (will be added
              to the database)
              <template v-if="getValueLengthFromDryRun(dryRun, 'toCreate')">
                <div
                  :style="{ display: 'inline' }"
                  v-if="modal.showToBeCreated"
                  @click="modal.showToBeCreated = false"
                >
                  <md-icon>
                    visibility
                  </md-icon>
                </div>
                <div :style="{ display: 'inline' }" v-else @click="modal.showToBeCreated = true">
                  <md-icon>
                    visibility_off
                  </md-icon>
                </div>
              </template>
              <ol v-if="modal.showToBeCreated" class="code-list">
                <li v-for="codeObj in getValueFromDryRun(dryRun, 'toCreate')" :key="codeObj.code">
                  {{ codeObj.code }}, {{ codeObj.workEmail || 'No associated email' }}
                </li>
              </ol>
            </li>
            <!-- Codes To Be Reactivated -->
            <li v-if="!failedForErrors(dryRun)">
              <div class="emphasis">
                {{ getValueLengthFromDryRun(dryRun, 'toReactivate') || 0 }}
              </div>
              {{ getValueLengthFromDryRun(dryRun, 'toReactivate') === 1 ? 'ID' : 'IDs' }} that were
              previously inactive (will be reinstated as active)
              <template v-if="getValueLengthFromDryRun(dryRun, 'toReactivate')">
                <div
                  :style="{ display: 'inline' }"
                  v-if="modal.showToBeReactivated"
                  @click="modal.showToBeReactivated = false"
                >
                  <md-icon>
                    visibility
                  </md-icon>
                </div>
                <div
                  :style="{ display: 'inline' }"
                  v-else
                  @click="modal.showToBeReactivated = true"
                >
                  <md-icon>
                    visibility_off
                  </md-icon>
                </div>
              </template>
              <ol v-if="modal.showToBeReactivated" class="code-list">
                <li v-for="codeObj in getValueFromDryRun(dryRun, 'toReactivate')" :key="codeObj.id">
                  {{ codeObj.code }}, {{ codeObj.workEmail || 'No associated email' }}
                </li>
              </ol>
            </li>
            <!-- Codes To Be Updated -->
            <li v-if="!failedForErrors(dryRun)">
              <div class="emphasis">{{ getValueLengthFromDryRun(dryRun, 'toUpdate') || 0 }}</div>
              updated {{ getValueLengthFromDryRun(dryRun, 'toUpdate') === 1 ? 'ID' : 'IDs' }}
              <template v-if="getValueLengthFromDryRun(dryRun, 'toUpdate')">
                <div
                  :style="{ display: 'inline' }"
                  v-if="modal.showToBeUpdated"
                  @click="modal.showToBeUpdated = false"
                >
                  <md-icon>
                    visibility
                  </md-icon>
                </div>
                <div :style="{ display: 'inline' }" v-else @click="modal.showToBeUpdated = true">
                  <md-icon>
                    visibility_off
                  </md-icon>
                </div>
              </template>
              <ol v-if="modal.showToBeUpdated" class="code-list">
                <li v-for="codeObj in getValueFromDryRun(dryRun, 'toUpdate')" :key="codeObj.id">
                  {{ codeObj.code }}, {{ codeObj.workEmail || 'No associated email' }}
                </li>
              </ol>
            </li>
            <!-- Codes To Be Ignored -->
            <li v-if="!failedForErrors(dryRun)">
              <div class="emphasis">
                {{ getValueLengthFromDryRun(dryRun, 'unchangedCodes') || 0 }}
              </div>
              existing {{ getValueLengthFromDryRun(dryRun, 'unchangedCodes') === 1 ? 'ID' : 'IDs' }}
              <template v-if="getValueLengthFromDryRun(dryRun, 'unchangedCodes')">
                <div
                  :style="{ display: 'inline' }"
                  v-if="modal.showToBeIgnored"
                  @click="modal.showToBeIgnored = false"
                >
                  <md-icon>
                    visibility
                  </md-icon>
                </div>
                <div :style="{ display: 'inline' }" v-else @click="modal.showToBeIgnored = true">
                  <md-icon>
                    visibility_off
                  </md-icon>
                </div>
              </template>
              <ol v-if="modal.showToBeIgnored" class="code-list">
                <li
                  v-for="codeObj in getValueFromDryRun(dryRun, 'unchangedCodes')"
                  :key="codeObj.id"
                >
                  {{ codeObj.code }}, {{ codeObj.workEmail || 'No associated email' }}
                </li>
              </ol>
            </li>
          </ul>
          <div v-if="!failedForErrors(dryRun)" class="top-margin">
            The file you uploaded is missing:
          </div>
          <ul>
            <!-- Codes To Be Deleted -->
            <li v-if="!failedForErrors(dryRun)">
              <div class="emphasis">{{ getValueLengthFromDryRun(dryRun, 'toDelete') || 0 }}</div>
              {{ getValueLengthFromDryRun(dryRun, 'toDelete') === 1 ? 'ID' : 'IDs' }} not associated
              with user accounts (will be removed from the database)
              <template v-if="getValueLengthFromDryRun(dryRun, 'toDelete')">
                <div
                  :style="{ display: 'inline' }"
                  v-if="modal.showToBeDeleted"
                  @click="modal.showToBeDeleted = false"
                >
                  <md-icon>
                    visibility
                  </md-icon>
                </div>
                <div :style="{ display: 'inline' }" v-else @click="modal.showToBeDeleted = true">
                  <md-icon>
                    visibility_off
                  </md-icon>
                </div>
              </template>
              <ol v-if="modal.showToBeDeleted" class="code-list">
                <li v-for="codeObj in getValueFromDryRun(dryRun, 'toDelete')" :key="codeObj.id">
                  {{ codeObj.code }}, {{ codeObj.workEmail || 'No associated email' }}
                </li>
              </ol>
            </li>
            <!-- Codes To Be Deactivated -->
            <li v-if="!failedForErrors(dryRun)">
              <div class="emphasis">
                {{ getValueLengthFromDryRun(dryRun, 'toDeactivate') || 0 }}
              </div>
              {{ getValueLengthFromDryRun(dryRun, 'toDeactivate') === 1 ? 'ID' : 'IDs' }} associated
              with user accounts (will be de-activated)
              <template v-if="getValueLengthFromDryRun(dryRun, 'toDeactivate')">
                <div
                  :style="{ display: 'inline' }"
                  v-if="modal.showToBeDeactivated"
                  @click="modal.showToBeDeactivated = false"
                >
                  <md-icon>
                    visibility
                  </md-icon>
                </div>
                <div
                  :style="{ display: 'inline' }"
                  v-else
                  @click="modal.showToBeDeactivated = true"
                >
                  <md-icon>
                    visibility_off
                  </md-icon>
                </div>
              </template>
              <ol v-if="modal.showToBeDeactivated" class="code-list">
                <li v-for="codeObj in getValueFromDryRun(dryRun, 'toDeactivate')" :key="codeObj.id">
                  {{ codeObj.code }}, {{ codeObj.workEmail || 'No associated email' }}
                </li>
              </ol>
            </li>
          </ul>
          <div v-if="!isConfirmDisabled()" class="top-margin">
            Please confirm to proceed with the changes outlined above.
          </div>
          <div class="buttons-container top-margin">
            <md-button @click="cancelFileFeed">
              Close
            </md-button>
            <md-button
              v-if="!isCancelDisabled()"
              class="confirm-upload proceed-button"
              @click="onCancelFeed"
              :disabled="isCancelDisabled()"
            >
              <i v-if="!cancelFeed">Cancel Import</i>
              <span v-else>
                <LoadSpinner :color="'white'" />
              </span>
            </md-button>
            <md-button
              v-if="!isConfirmDisabled()"
              class="confirm-upload proceed-button"
              @click="onActualRun"
            >
              <i v-if="!actualRun.loading">Confirm</i>
              <span v-else>
                <LoadSpinner :color="'white'" />
              </span>
            </md-button>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { LoadSpinner } from '@/components'
import EmployeeCode from '@/services/employeeCodes'
import EmployeeCodeImport from '@/services/employeeCodeImport'
import CollectionManager from '@/services/collectionManager'
import SelectOrganization from '@/services/selectOrganizations'
import Modal from '@/components/Modal'
import Pagination from '@/services/pagination'

// this is used to employ resetViewState()
function getDefaultData(nonResettingState = {}) {
  return {
    ...nonResettingState,
    options: EmployeeCodeImport.options,
    selectedFile: null,
    selectedDownloadOrganization: {
      name: null,
      id: null,
    },
    selectedFileFeedOrganization: {
      name: null,
      id: null,
    },
    selectedSubOrganization: {
      name: null,
      id: null,
    },
    modal: {
      isOpen: false,
      showToBeCreated: false,
      showErrors: false,
      showToBeReactivated: false,
      showToBeIgnored: false,
      showToBeUpdated: false,
      showToBeDeleted: false,
      showToBeDeactivated: false,
    },
    dryRun: {
      loading: false,
      data: null,
    },
    cancelFeed: false,
    actualRun: {
      loading: false,
    },
    employeeCodeImports: CollectionManager.create({
      ModelClass: EmployeeCodeImport,
      pagination: { pageSize: 1000 },
    }),
    errors: null,
  }
}

const nonResettingState = {
  organizations: CollectionManager.create({
    ModelClass: SelectOrganization,
    pagination: Pagination.create({ pageSize: 100 }),
    refreshing: true,
    // filters: { ordering: '-datetime_created' }, TODO(Bruno 4-3-2020): filter alphabetically
  }),
}

export default {
  name: 'EligibilityFileFeed',
  components: { LoadSpinner, Modal },
  data() {
    return getDefaultData(nonResettingState)
  },
  created() {
    this.fetchOrganizations()
    this.employeeCodeImports.refresh()
  },
  methods: {
    getValueFromDryRun(dryRun, value) {
      return dryRun && dryRun[value]
    },
    getValueLengthFromDryRun(dryRun, value) {
      return dryRun && dryRun[value] && dryRun[value].length
    },
    downloadSuborganizations() {
      if (this.user.isStaff) {
        return (
          (this.selectedDownloadOrganization &&
            this.selectedDownloadOrganization.subOrganizations) ||
          []
        )
      }

      if (this.user.isAdmin && !this.user.subOrganization) {
        return this.organizationDetails.subOrganizations || []
      }

      return []
    },
    fileFeedSuborganizations() {
      if (this.user.isStaff) {
        return (
          (this.selectedFileFeedOrganization &&
            this.selectedFileFeedOrganization.subOrganizations) ||
          []
        )
      }

      if (this.user.isAdmin && !this.user.subOrganization) {
        return this.organizationDetails.subOrganizations || []
      }

      return []
    },
    failedForErrors(dryRun) {
      return dryRun && dryRun.errors && dryRun.errors.length > 0
    },
    onFileFeedOrganizationSelection({ target: { value } }) {
      let targetOrg = this.organizations.list.find(org => org.id === value)
      this.selectedFileFeedOrganization = targetOrg
    },
    onDownloadOrganizationSelection({ target: { value } }) {
      let targetOrg = this.organizations.list.find(org => org.id === value)
      this.selectedDownloadOrganization = targetOrg
    },
    filterBySelectedOrganization(list, organizationId) {
      if (!organizationId) {
        return list
      }

      return list.filter(fileUpload => {
        return fileUpload.organizationId === organizationId
      })
    },
    fetchOrganizations() {
      if (this.user.isStaff) {
        this.organizations.refresh()
      }

      if (this.user.isAdmin)
        this.selectedDownloadOrganization = this.user.organizationRef
    },
    handleEligibilityListDownload() {
      if (this.user.isAdmin) {
        if(!this.selectedDownloadOrganization.id)
          this.selectedDownloadOrganization.id = this.user.organizationRef.id
        if(this.user.subOrganization && !this.selectedSubOrganization.id)
          this.selectedSubOrganization.id = this.user.subOrganization.id
      }
      EmployeeCode.api.getCurrentEligibilityList(
        this.selectedDownloadOrganization.id,
        this.selectedSubOrganization.id,
      )
    },
    onSubOrganizationSelection({ target: { value } }) {
      this.selectedSubOrganization = {
        id: value,
      }
    },
    handleTemplateDownload() {
      window.open('/static/employee-codes-template.csv')
    },
    cancelFileFeed() {
      this.modal.isOpen = false
    },
    showFileUpload(uploadId) {
      this.fileUpload =
        this.employeeCodeImports &&
        this.employeeCodeImports.list &&
        this.employeeCodeImports.list.find(upload => {
          return upload.id === uploadId
        })
      this.dryRun = this.fileUpload.dryRunResults
      this.modal.isOpen = true
    },
    onCancelFeed() {
      this.cancelFeed = true
      EmployeeCode.api
        .cancelFileFeedUpload(this.fileUpload.id)
        .then(() => {
          this.success = true
          this.$Alert.alert({
            type: 'success',
            message: `<h4>Import Canceled!</h4>`,
            timeout: 6000,
          })
        })
        .catch(this.uploadErrorHandler)
        .finally(() => {
          this.cancelFeed = false
          this.employeeCodeImports.refresh()
          this.modal.isOpen = false
        })
    },
    onActualRun() {
      this.actualRun.loading = true
      EmployeeCode.api
        .confirmFileFeedUpload(this.fileUpload.id)
        .then(() => {
          this.success = true
          this.$Alert.alert({
            type: 'success',
            message: `<h4>Import Successful!</h4>`,
            timeout: 6000,
          })
        })
        .catch(this.uploadErrorHandler)
        .finally(() => {
          this.actualRun.loading = false
          this.employeeCodeImports.refresh()
          this.modal.isOpen = false
        })
    },
    isConfirmDisabled() {
      return this.fileUpload.importStatus !== 'MASS_CHANGE'
    },
    isCancelDisabled() {
      return this.fileUpload.importStatus !== 'MASS_CHANGE'
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/containers';

.eligibility-uploader {
  @include screen-container($mobile-padding: 1rem 0.5rem, $desktop-max-width: 112rem);
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: center;
}

.import-card {
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  border: 1px solid #e4eeee;
  border-radius: 5px;
}

.card-header {
  display: flex;
  flex-flow: row;
  align-items: center;
  padding: 0 30px;
  border-bottom: 1px solid #e4eeee;

  h2 {
    margin: 30px auto 30px 0;
    font-weight: 800;
  }

  .md-button {
    background-color: #fff;
    color: #a1a1a6;
    border: solid 1px #a1a1a6;
    border-radius: 5px;
  }
}

.card-body {
  padding: 30px 30px;

  p {
    margin-bottom: 30px;
  }
}

.copy-list {
  margin-bottom: 2rem;
}

.emphasis {
  display: inline;
  text-decoration: underline;
  font-weight: bold;
}

.bold {
  display: inline;
  font-weight: bold;
}

.top-margin {
  margin-top: 1.5rem;
}

.selections-container {
  padding-left: 30px;
  padding-right: 30px;
  margin-bottom: 30px;
  display: flex;
  flex-flow: row;
  align-items: center;
}

.organizations {
  margin-left: 0.5rem;
  width: 300px;
  height: 36px;
  font-family: $base-font-family;
  font-size: 1rem;
}

.suborganizations {
  margin-left: 0.5rem;
  width: 300px;
  height: 36px;
  font-family: $base-font-family;
  font-size: 1rem;
}

.placeholder {
  margin-left: auto;
  width: 300px;
  height: 36px;
  font-family: $base-font-family;
  font-size: 1rem;
  border-radius: 5px;
  border: 1px solid #a6a6a6;
  background-color: #f8f8f8;
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: center;
}

.current-file {
  .label {
    margin-right: 25px;
    font-size: 1rem;
  }

  .file-name {
    font-size: 1rem;
  }
}

.errors-container {
  .error {
    color: $ruby;
  }
}

.buttons-container {
  display: flex;
  flex-flow: row;
  align-items: center;

  .choose-file-button {
    margin-right: auto;
    background-color: #000;
    color: #fff;
    border-radius: 5px;

    &:disabled {
      background-color: $mid-gray;
    }
  }

  .confirm-upload {
    margin-left: auto;
    margin-right: 2rem;
  }

  .proceed-button {
    background-color: $blue;
    color: #fff;
    border-radius: 5px;

    &:disabled {
      background-color: $mid-gray;
    }
  }
}

.success-header {
  text-align: center;
  font-weight: 800;
}

.list-container {
  margin-bottom: 30px;

  ul {
    margin-left: 50px;

    li {
      font-family: $base-font-family;
      margin: 5px 0;
    }
  }

  p {
    font-family: $base-font-family;
    margin-left: 50px;
  }
}

.button-container {
  display: flex;
  flex-flow: row;

  .md-button {
    margin-left: auto;
    background-color: #000;
    color: #fff;
    border-radius: 5px;
  }
}

.code-list {
  background-color: $soft-gray;
  border: 1px solid $gray;
  border-radius: 3px;
  margin-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 1rem;
  max-height: 10rem;
  overflow-y: auto;
}

.clickable-upload {
  cursor: pointer;
}

.name-and-status {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.download-helper-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.orgs-selection {
  display: flex;
  flex-direction: row;
  align-items: center;
}
</style>
